import { useEffect, useState } from 'react';
import {
  useForm,
  FormProvider,
  Controller,
} from 'react-hook-form';
import Drawer from '~/components/Drawer';
import { updateCampaignStatus } from '~/services/campaign';
import { toast } from 'react-hot-toast';
import { getCampaignStatusBadgeClass } from '~/utils/campaignBadgeStyles';
import { formatDate, parseISO } from 'date-fns';

const statusOptions = [
  {
    label: 'Inactive',
    value: 'inactive',
    description:
      'New campaign placeholder for a CampaignRequest that was not a prescreen-call and has patient information but is not yet Approved or Rejected.',
  },
  {
    label: 'Unpublished',
    value: 'unpublished',
    description:
      'Available to patient and team members, but not to the public; waiting for Campaign Request to be Approved or Rejected.',
  },
  {
    label: 'Approved',
    value: 'approved',
    description:
      'Approved to start fund raising, but does not yet have 1st donation. (FUTURE)',
  },
  {
    label: 'Open',
    value: 'funded',
    description:
      'Campaign created and live, but medical professional verification is not complete.',
  },
  {
    label: 'Disbursable',
    value: 'disbursable',
    description:
      'Medical professional verification AND patient agreements are complete; campaign is able to request disbursements.',
  },
  {
    label: 'Closed',
    value: 'closed',
    description: 'Campaign is closed.',
  },
  {
    label: 'Archived',
    value: 'archived',
    description: 'Campaign is archived. (FUTURE)',
  },
];

// A campaign only reaches these statuses by approving its application, which is
// what assigns the NTAF ID, creates the NXT fund and publishes the campaign
// (HHLTB-2717). The API rejects the jump; this keeps the options out of reach.
const APPROVAL_GATED_STATUSES = ['approved', 'funded', 'disbursable'];
const PRE_APPROVAL_STATUSES = ['inactive', 'unpublished'];

interface CampaignStatus {
  id?: number;
  code?: string;
  name?: string;
}

interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  campaign?: {
    id?: number;
    campaign_id?: number;
    campaign_status?: CampaignStatus | string;
    ntaf_id?: string;
    unpublished_date?: string;
    approved_date?: string;
    funded_date?: string;
    disbursable_date?: string;
    closed_date?: string;
    reopened_date?: string;
  };
  onStatusUpdate?: (newStatus: CampaignStatus) => void;
}

interface FormData {
  status: string;
}

const StatusDrawer: React.FC<Props> = ({ isOpen, setIsOpen, campaign, onStatusUpdate }) => {
  const [saving, setSaving] = useState(false);

  // Get the current status code from the campaign
  const getCurrentStatusCode = (): string => {
    if (!campaign?.campaign_status) return '';
    if (typeof campaign.campaign_status === 'string') {
      // If it's a string, convert to lowercase to match our option values
      return campaign.campaign_status.toLowerCase();
    }
    return campaign.campaign_status.code || '';
  };

  // Get the current status name
  const getCurrentStatusName = (): string => {
    if (!campaign?.campaign_status) return '';
    if (typeof campaign.campaign_status === 'string') {
      return campaign.campaign_status;
    }
    return campaign.campaign_status.name || '';
  };

  // Determine the prior-to-closed status based on the most recent status date before closed
  const getPriorToClosedStatus = (): { code: string; name: string; date: string } | null => {
    if (!campaign) return null;

    const statusDates = [
      { code: 'disbursable', name: 'Disbursable', date: campaign.disbursable_date },
      { code: 'funded', name: 'Open', date: campaign.funded_date },
      { code: 'approved', name: 'Approved', date: campaign.approved_date },
      { code: 'unpublished', name: 'Unpublished', date: campaign.unpublished_date },
    ];

    // Filter out null dates and sort by date descending
    const validStatuses = statusDates
      .filter(s => s.date != null)
      .sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());

    return validStatuses.length > 0 ? validStatuses[0] : null;
  };

  const currentStatus = getCurrentStatusCode();
  const currentStatusName = getCurrentStatusName();
  const isClosed = currentStatus === 'closed';
  const priorStatus = isClosed ? getPriorToClosedStatus() : null;
  const needsApprovalFirst =
    PRE_APPROVAL_STATUSES.includes(currentStatus) && !campaign?.ntaf_id;

  const methods = useForm<FormData>({
    defaultValues: {
      status: getCurrentStatusCode(),
    },
  });

  // Reset form when drawer opens or campaign changes
  useEffect(() => {
    if (isOpen) {
      methods.reset({
        status: getCurrentStatusCode(),
      });
    }
  }, [isOpen, campaign?.campaign_status]);

  const onSubmit = async (data: FormData) => {
    const campaignId = campaign?.id || campaign?.campaign_id;
    if (!campaignId) {
      toast.error('Campaign ID not found');
      return;
    }

    setSaving(true);
    toast.loading('Updating status...');

    try {
      const resp = await updateCampaignStatus(campaignId, data.status);
      toast.dismiss();

      if (resp.detail) {
        toast.error(resp.detail);
        setSaving(false);
        return;
      }

      toast.success('Campaign status updated successfully');

      // Call the onStatusUpdate callback if provided
      if (onStatusUpdate) {
        onStatusUpdate(resp);
      }

      setIsOpen(false);
    } catch (error: any) {
      toast.dismiss();
      console.error('Error updating campaign status:', error);
      toast.error(error.message || 'Failed to update campaign status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title={`Update Status`}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {isClosed && priorStatus && campaign?.closed_date && (
            <div className='mb-[20px] rounded-md bg-[#F4F4F5] p-[16px]'>
              <div className='flex items-center justify-between mb-[12px]'>
                <span className='text-[12px]/[16px] text-[#949599]'>
                  {formatDate(parseISO(campaign.closed_date), 'MM/dd/yy')}
                </span>
                <div className='flex items-center gap-[8px]'>
                  <span className='text-[14px]/[20px] font-semibold'>Current Status:</span>
                  <span className={`inline-flex rounded-full px-2 py-1 text-sm ${getCampaignStatusBadgeClass(currentStatusName)}`}>
                    {currentStatusName}
                  </span>
                </div>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-[12px]/[16px] text-[#949599]'>
                  {formatDate(parseISO(priorStatus.date), 'MM/dd/yy')}
                </span>
                <div className='flex items-center gap-[8px]'>
                  <span className='text-[14px]/[20px] font-semibold'>Prior-to-Closed Status:</span>
                  <span className={`inline-flex rounded-full px-2 py-1 text-sm ${getCampaignStatusBadgeClass(priorStatus.name)}`}>
                    {priorStatus.name}
                  </span>
                </div>
              </div>
            </div>
          )}
          {needsApprovalFirst && (
            <div className='mb-[20px] rounded-md bg-[#FDF3E0] p-[16px] text-[12px]/[18px]'>
              This campaign&apos;s application has not been approved yet, so it has
              no NTAF ID. Approve the application to move it forward — approving is
              what assigns the NTAF ID, creates the fund in NXT and publishes the
              campaign.
            </div>
          )}
          {statusOptions.map((status) => (
            <div key={status.value} className='mt-[12px]'>
              <div className='align-center flex gap-[12px]'>
                <Controller
                  name='status'
                  control={methods.control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      id={`td-${status.value}`}
                      type='radio'
                      value={status.value}
                      checked={field.value === status.value}
                      onChange={() => field.onChange(status.value)}
                      disabled={
                        needsApprovalFirst &&
                        APPROVAL_GATED_STATUSES.includes(status.value)
                      }
                      className='mt-[2px] h-[18px] w-[18px] disabled:cursor-not-allowed'
                    />
                  )}
                />
                <label
                  htmlFor={`td-${status.value}`}
                  className={
                    needsApprovalFirst &&
                    APPROVAL_GATED_STATUSES.includes(status.value)
                      ? 'text-[#949599]'
                      : ''
                  }
                >
                  {status.label}
                </label>
              </div>
              <div className='ml-[28px] p-[4px] text-[12px]'>
                {status.description}
              </div>
            </div>
          ))}
          <div className='mb-[30px] flex gap-[16px]'>
            <button
              type='button'
              className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
              onClick={() => setIsOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:opacity-50'
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </FormProvider>
    </Drawer>
  );
};

export default StatusDrawer;
